import { Link, useParams } from 'react-router-dom';
import useSWR from 'swr';

import { PageHeader } from '../../../components/PageHeader';
import { invariant, nullthrows } from '../../../utils/invariant';
import { fetchCustomerPortalQuotationRequest } from './models';
import { DataField } from '../../../components/DataField';
import { formatDate } from '../../../utils/date';
import { Breadcrumb } from '../../../components/Breadcrumb';
import { TrailerTypeIcon } from '../../order/pages/order/TrailerTypes';
import { useTranslation } from '../../../contexts/translation-context';
import { PageHeading } from 'components/PageHeading';
import { useCustomerPortalCustomer } from '../CustomerPortalCustomerContext';
import { SpinnerBlock } from '../../../components/Spinner';

export const CustomerPortalQuotationRequestPage = () => {
  const { i18n } = useTranslation();
  const { customer } = useCustomerPortalCustomer();
  const { quotationRequestId } = useParams<{ quotationRequestId: string }>();
  invariant(quotationRequestId);

  const { data, isLoading } = useSWR(['customer-portal-quotation-request', customer.id, quotationRequestId], () =>
    fetchCustomerPortalQuotationRequest({ id: quotationRequestId, customerId: customer.id }),
  );

  if (isLoading) {
    return <SpinnerBlock message={i18n('customerPortal.loading')} />;
  }

  const quotation = nullthrows(data, i18n('customerPortal.quotes.notFound'));
  const title = i18n('customerPortal.quotes.requestTitle', {
    reference: quotation.reference,
  });

  return (
    <>
      <PageHeader title={title} />

      <PageHeading
        leftSide={
          <Breadcrumb
            parentItem={{
              name: i18n('customerPortal.quotes.requests'),
              to: '..',
            }}
            currentItem={title}
          />
        }
      />

      <div className="px-4">
        <div className="flex gap-4 mb-8">
          <DataField title={i18n('customerPortal.quotes.type')}>{quotation.type}</DataField>
          <DataField title={i18n('customerPortal.quotes.quoteReference')}>{quotation.reference}</DataField>
          <DataField title={i18n('customerPortal.quotes.requestedAt')}>{formatDate(quotation.createdAt)}</DataField>
          <DataField title={i18n('customerPortal.quotes.quote')}>
            {quotation.quotationId ? <Link to={`../../${quotation.quotationId}`}>{quotation.reference}</Link> : 'n/a'}
          </DataField>
        </div>

        {quotation.notes.length > 0 && (
          <div className="mb-4">
            <DataField title={i18n('customerPortal.quotes.notes')}>
              <span className="whitespace-pre-wrap">{quotation.notes}</span>
            </DataField>
          </div>
        )}

        <div>
          <div className="grid grid-cols-8 font-medium mb-2">
            <div className="col-span-3">{i18n('customerPortal.quotes.departurePlace')}</div>
            <div className="col-span-3">{i18n('customerPortal.quotes.destination')}</div>
          </div>

          {quotation.lines.map((line) => {
            return (
              <div className="my-1" key={line.id}>
                <div className="grid grid-cols-8">
                  <div className="col-span-3">{`${line.departureCountry}${line.departurePostalCode} ${line.departureCity}`}</div>
                  <div className="col-span-3">{`${line.arrivalCountry}${line.arrivalPostalCode} ${line.arrivalCity}`}</div>
                </div>

                <div className="flex flex-wrap gap-2 mt-2">
                  {line.trailerTypes.map((trailerType) => {
                    return (
                      <div
                        className="bg-neutral-subtle rounded flex flex-col items-center justify-center px-2 py-1"
                        key={`${line.id}-${trailerType}`}
                      >
                        <TrailerTypeIcon type={trailerType} variant="small" />
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};
